import React, { useEffect } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { Phone, ArrowRight, MapPin, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { DISTRICTS, getDistrict, districtPath } from "@/data/districts";
import {
  Navbar,
  TrustBar,
  Process,
  LeadForm,
  FinalCTA,
  Footer,
  StickyCall,
  PHONE_DISPLAY,
  PHONE_TEL,
} from "@/pages/LandingPage";

const SITE = "https://rodex-berlin.de";
const scrollToForm = () => document.getElementById("kontakt")?.scrollIntoView({ behavior: "smooth" });

const setMeta = (selector, attr, value) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement(selector.startsWith("link") ? "link" : "meta");
    const [, key, val] = selector.match(/\[(\w+)="([^"]+)"\]/);
    el.setAttribute(key, val);
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
};

const useDistrictSeo = (d) => {
  useEffect(() => {
    if (!d) return;
    const prevTitle = document.title;
    const keys = ['meta[name="description"]', 'meta[property="og:title"]', 'meta[property="og:description"]', 'meta[property="og:url"]'];
    const prev = keys.map((k) => document.head.querySelector(k)?.getAttribute("content"));
    const url = `${SITE}${districtPath(d.slug)}`;
    const title = `Rattenbekämpfung & Mäusebekämpfung ${d.name} | Rodex Berlin`;
    const desc = `Ratten oder Mäuse in Berlin-${d.name}? Rodex Berlin — zertifizierter Spezialist für Nagetierbekämpfung. Diskret, nach BfR-Leitlinien, Reaktion innerhalb 24 h. ☎ ${PHONE_DISPLAY}`;
    document.title = title;
    setMeta('meta[name="description"]', "content", desc);
    setMeta('link[rel="canonical"]', "href", url);
    setMeta('meta[property="og:title"]', "content", title);
    setMeta('meta[property="og:description"]', "content", desc);
    setMeta('meta[property="og:url"]', "content", url);

    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.id = "district-jsonld";
    ld.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      serviceType: "Rattenbekämpfung und Mäusebekämpfung",
      name: `Nagetierbekämpfung in Berlin-${d.name}`,
      url,
      provider: { "@type": "LocalBusiness", name: "Rodex Berlin", telephone: PHONE_TEL, url: SITE },
      areaServed: [{ "@type": "City", name: `Berlin-${d.name}` }, ...d.ortsteile.map((o) => ({ "@type": "Place", name: o }))],
    });
    document.head.appendChild(ld);
    window.scrollTo({ top: 0, behavior: "instant" });
    return () => {
      document.title = prevTitle;
      ld.remove();
      setMeta('link[rel="canonical"]', "href", `${SITE}/`);
      keys.forEach((k, i) => prev[i] && setMeta(k, "content", prev[i]));
    };
  }, [d]);
};

const DistrictHero = ({ d }) => (
  <section className="relative hero-radial text-white pt-32 pb-16 lg:pt-40 lg:pb-24 overflow-hidden" data-testid="district-hero">
    <div className="absolute inset-0 grain opacity-40 pointer-events-none" />
    <div className="absolute top-20 right-[-100px] w-[500px] h-[500px] rounded-full bg-brand-orange/15 blur-3xl pointer-events-none" />
    <div className="relative max-w-7xl mx-auto px-5 lg:px-8 fade-up">
      <nav className="text-xs text-white/50 mb-6" data-testid="district-breadcrumb">
        <Link to="/" className="hover:text-white">Startseite</Link>
        <span className="mx-2">/</span>
        <span className="text-white/80">Rattenbekämpfung {d.name}</span>
      </nav>
      <Badge className="bg-brand-orange/15 text-brand-orange hover:bg-brand-orange/20 border border-brand-orange/30 rounded-full px-3 py-1.5 mb-6 text-xs sm:text-sm">
        <MapPin className="w-3.5 h-3.5 mr-1.5" /> Berlin-{d.name} · {d.tagline}
      </Badge>
      <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.08] tracking-tight mb-6 max-w-4xl" data-testid="district-title">
        Rattenbekämpfung & Mäusebekämpfung in <span className="text-brand-orange">{d.name}.</span>
      </h1>
      <p className="text-sm sm:text-base lg:text-lg text-white/75 max-w-2xl mb-8 leading-relaxed">
        Ihr zertifizierter Spezialist für Nagetiere — schnell vor Ort in {d.name}, diskret und nach BfR-Leitlinien.
        Reaktion innerhalb 24 Stunden.
      </p>
      <div className="flex flex-col sm:flex-row gap-3">
        <Button size="lg" onClick={scrollToForm} className="bg-brand-orange hover:bg-brand-orange-deep text-white rounded-full h-12 sm:h-14 px-8 font-semibold shadow-lg shadow-brand-orange/30" data-testid="district-cta-form">
          Kostenlose Beratung anfordern <ArrowRight className="ml-2 w-5 h-5" />
        </Button>
        <a href={`tel:${PHONE_TEL}`} data-testid="district-cta-phone">
          <Button size="lg" variant="outline" className="w-full sm:w-auto border-white/30 bg-white/5 hover:bg-white/10 text-white rounded-full h-12 sm:h-14 px-8 font-semibold backdrop-blur">
            <Phone className="mr-2 w-5 h-5" /> {PHONE_DISPLAY}
          </Button>
        </a>
      </div>
    </div>
  </section>
);

const DistrictIntro = ({ d }) => (
  <section id="leistungen" className="py-20 lg:py-24 bg-white" data-testid="district-intro">
    <div className="max-w-6xl mx-auto px-5 lg:px-8 grid lg:grid-cols-5 gap-12">
      <div className="lg:col-span-3 space-y-5 text-slate-600 leading-relaxed">
        <div className="text-brand-orange text-sm font-semibold uppercase tracking-[0.2em]">Nagetierbekämpfung vor Ort</div>
        <h2 className="text-3xl lg:text-4xl font-bold text-charcoal tracking-tight">Ratten und Mäuse in {d.name}? Wir helfen.</h2>
        {d.intro.map((p, i) => <p key={i}>{p}</p>)}
      </div>
      <div className="lg:col-span-2 bg-slate-50 rounded-2xl p-7 border border-slate-100" data-testid="district-hotspots">
        <h3 className="font-bold text-charcoal text-lg mb-4 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-brand-orange" /> Typische Problemstellen in {d.name}
        </h3>
        <ul className="space-y-3 text-sm text-slate-600">
          {d.hotspots.map((h) => (
            <li key={h} className="flex gap-3"><span className="w-1.5 h-1.5 rounded-full bg-brand-orange mt-2 flex-shrink-0" />{h}</li>
          ))}
        </ul>
      </div>
    </div>
  </section>
);

const DistrictOrtsteile = ({ d }) => (
  <section className="py-16 bg-slate-50 border-y border-slate-100" data-testid="district-ortsteile">
    <div className="max-w-6xl mx-auto px-5 lg:px-8">
      <h2 className="text-2xl lg:text-3xl font-bold text-charcoal tracking-tight mb-6">Wir sind in allen Ortsteilen von {d.name} im Einsatz</h2>
      <div className="flex flex-wrap gap-2">
        {d.ortsteile.map((o) => (
          <span key={o} className="px-4 py-2 rounded-full bg-white border border-slate-200 text-sm font-medium text-charcoal">{o}</span>
        ))}
      </div>
    </div>
  </section>
);

const DistrictFaq = ({ d }) => (
  <section id="faq" className="py-20 bg-white" data-testid="district-faq">
    <div className="max-w-3xl mx-auto px-5 lg:px-8">
      <div className="text-brand-orange text-sm font-semibold uppercase tracking-[0.2em] mb-3">Häufige Fragen</div>
      <h2 className="text-3xl lg:text-4xl font-bold text-charcoal tracking-tight mb-8">Fragen aus {d.name}</h2>
      <Accordion type="single" collapsible className="space-y-3">
        {d.faqs.map((f, i) => (
          <AccordionItem key={f.q} value={`f${i}`} className="border border-slate-200 rounded-xl px-5 bg-white" data-testid={`district-faq-item-${i}`}>
            <AccordionTrigger className="text-left font-semibold text-charcoal hover:no-underline">{f.q}</AccordionTrigger>
            <AccordionContent className="text-slate-600 leading-relaxed">{f.a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  </section>
);

const OtherDistricts = ({ d }) => (
  <section className="py-14 bg-slate-50 border-t border-slate-100" data-testid="district-others">
    <div className="max-w-6xl mx-auto px-5 lg:px-8">
      <h2 className="text-lg font-bold text-charcoal mb-4">Rodex Berlin in weiteren Bezirken</h2>
      <div className="flex flex-wrap gap-2">
        {DISTRICTS.filter((x) => x.slug !== d.slug).map((x) => (
          <Link key={x.slug} to={districtPath(x.slug)} className="px-4 py-2 rounded-full border border-slate-200 bg-white text-sm text-charcoal hover:border-brand-orange hover:text-brand-orange transition-colors" data-testid={`district-other-link-${x.slug}`}>
            {x.name}
          </Link>
        ))}
      </div>
    </div>
  </section>
);

export default function DistrictPage() {
  const { slug } = useParams();
  const d = getDistrict(slug);
  useDistrictSeo(d);
  if (!d) return <Navigate to="/" replace />;

  return (
    <div className="bg-background min-h-screen" data-testid="district-page">
      <Navbar base="/" />
      <main>
        <DistrictHero d={d} />
        <TrustBar />
        <DistrictIntro d={d} />
        <DistrictOrtsteile d={d} />
        <Process />
        <DistrictFaq d={d} />
        <LeadForm district={d.name} />
        <OtherDistricts d={d} />
        <FinalCTA />
      </main>
      <Footer />
      <StickyCall />
    </div>
  );
}
